import type { KeyboardEvent, PointerEvent } from 'react'
import { formatDuration } from '../../lib/audio'
import { flatPeaks, resamplePeaks } from '../../lib/peaks'

// Seekable audio displays: the voice memo's static waveform (design 4b —
// played bars wine, unplayed wine/30) and the narration card's thin progress
// line. Both are sliders for keyboard and screen-reader users.

interface SeekProps {
  /** Seconds. */
  time: number
  /** Seconds. */
  duration: number
  onSeek?(seconds: number): void
  /** Accessible name, e.g. "Seek voice memo". */
  label: string
}

function seekHandlers({ time, duration, onSeek }: SeekProps) {
  const seekable = !!onSeek && duration > 0
  const onPointerDown = (e: PointerEvent<HTMLElement>) => {
    if (!seekable) return
    const rect = e.currentTarget.getBoundingClientRect()
    if (rect.width <= 0) return
    const f = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width))
    onSeek!(f * duration)
  }
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (!seekable) return
    const step = Math.max(1, Math.min(10, duration / 20))
    let next: number | null = null
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = time + step
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = time - step
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = duration
    if (next === null) return
    e.preventDefault()
    onSeek!(Math.min(duration, Math.max(0, next)))
  }
  return { seekable, onPointerDown, onKeyDown }
}

function sliderProps({ time, duration, label }: SeekProps, seekable: boolean) {
  return {
    role: 'slider' as const,
    tabIndex: seekable ? 0 : -1,
    'aria-label': label,
    'aria-valuemin': 0,
    'aria-valuemax': Math.round(duration),
    'aria-valuenow': Math.round(Math.min(time, duration)),
    'aria-valuetext': `${formatDuration(time)} of ${formatDuration(duration)}`,
    'aria-disabled': seekable ? undefined : true,
  }
}

export function Waveform({
  peaks,
  height = 22,
  maxBars = 40,
  className = '',
  ...seek
}: SeekProps & { peaks?: number[] | null; height?: number; maxBars?: number; className?: string }) {
  const bars = resamplePeaks(peaks && peaks.length > 0 ? peaks : flatPeaks(24), maxBars)
  const progress = seek.duration > 0 ? Math.min(1, seek.time / seek.duration) : 0
  const { seekable, onPointerDown, onKeyDown } = seekHandlers(seek)
  return (
    <div
      {...sliderProps(seek, seekable)}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      style={{ height }}
      className={`flex touch-none items-end gap-[2px] rounded-[3px] ${seekable ? 'cursor-pointer' : ''} ${className}`}
    >
      {bars.map((p, i) => (
        <span
          key={i}
          aria-hidden
          style={{ height: Math.max(3, Math.round(Math.min(1, Math.max(0, p)) * height)) }}
          className={`w-[3px] shrink-0 rounded-[1px] transition-colors duration-150 ${
            (i + 0.5) / bars.length <= progress ? 'bg-primary' : 'bg-primary/30'
          }`}
        />
      ))}
    </div>
  )
}

/** The thin progress line under the narration card's title (3px, wine on ink/12). */
export function Scrubber({ className = '', ...seek }: SeekProps & { className?: string }) {
  const progress = seek.duration > 0 ? Math.min(1, seek.time / seek.duration) : 0
  const { seekable, onPointerDown, onKeyDown } = seekHandlers(seek)
  return (
    <div
      {...sliderProps(seek, seekable)}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      // A taller hit area around the 3px line.
      className={`group relative flex h-3 touch-none items-center ${seekable ? 'cursor-pointer' : ''} ${className}`}
    >
      <span aria-hidden className="relative h-[3px] w-full overflow-hidden rounded-[2px] bg-foreground/12">
        <span className="absolute inset-y-0 left-0 rounded-[2px] bg-primary" style={{ width: `${progress * 100}%` }} />
      </span>
    </div>
  )
}
